'use client';

import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { useInView } from 'framer-motion';
import 'swiper/css';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperProps, SwiperSlide } from 'swiper/react';

import { SlideNext, SlidePrev } from '@/blocks/components/Slider/Arrows';
import '@/blocks/components/Slider/Pagination/swiperPagination.css';

import styles from './CardsSlider.module.scss';

export interface SlideData {
  isActive: boolean;
  isVisible: boolean;
  isPrev: boolean;
  isNext: boolean;
}

interface CardsSliderProps<T> {
  renderCard(card: T, slideData?: SlideData): ReactNode;

  items: T[];
  itemKey?: keyof T;
  swiperProps?: Omit<SwiperProps, 'navigation' | 'pagination'>;
  autoplayDelay?: number;
  classes?: {
    slide?: string;
    slider?: string;
    paginationWrapper?: string;
  };
}

/**
 * ## Sizing Control
 * Choose one sizing method:
 *
 * 1. **Fixed slides per view**
 * ```jsx
 * swiperProps={{ slidesPerView: 3 }}
 * ```
 *
 * 2. **Dynamic Width Slides (CSS-controlled)**
 * ```jsx
 * swiperProps={{ slidesPerView: 'auto' }}
 * classes={{ slide: 'w-[300px]' }}
 * ```
 *
 * ## Requirements
 * - Card components must use full parent dimensions:
 * ```css
 * width: 100%;
 * height: 100%;
 * ```
 * - Avoid fixed dimensions in renderCard outputs
 */
export function CardsSlider<T>({
  items,
  renderCard,
  itemKey,
  swiperProps,
  autoplayDelay = 2500,
  classes,
}: CardsSliderProps<T>) {
  const containerRef = useRef(null);
  const inView = useInView(containerRef);
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!swiper) {
      return;
    }

    if (inView) {
      swiper?.autoplay?.start();
    } else {
      swiper?.autoplay?.stop();
    }
  }, [inView, swiper]);

  return (
    <div
      className={styles.wrapper}
      style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.4s' }}
      ref={containerRef}
    >
      <div className={styles.sliderWrapper}>
        <SlidePrev variant={'chevron'} className={styles.prev} />
        <Swiper
          className={classes?.slider || styles.slider}
          onSwiper={setSwiper}
          modules={[Pagination, Navigation, Autoplay]}
          autoplay={{
            delay: autoplayDelay,
            pauseOnMouseEnter: true,
          }}
          pagination={{ el: `.${styles.pagination}`, clickable: true }}
          navigation={{
            prevEl: `.${styles.prev}`,
            nextEl: `.${styles.next}`,
          }}
          loop={true}
          slidesPerView={1.1}
          centeredSlides={true}
          spaceBetween={8}
          speed={500}
          breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 30,
              centeredSlides: false,
            },

            992: {
              slidesPerView: 'auto',
              spaceBetween: 30,
              centeredSlides: false,
            },
          }}
          {...swiperProps}
        >
          {items.map((item, i) => {
            return (
              <SwiperSlide
                key={itemKey ? String(item[itemKey]) : i}
                className={classes?.slide || styles.slide}
              >
                {(slideData) => renderCard(item, slideData)}
              </SwiperSlide>
            );
          })}
        </Swiper>
        <SlideNext variant={'chevron'} className={styles.next} />
      </div>
      <div className={classes?.paginationWrapper || styles.paginationWrapper}>
        <div className={styles.pagination}></div>
      </div>
    </div>
  );
}
